import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ChatCircleDots, UserCirclePlus, Storefront, Receipt, Lightning } from "@phosphor-icons/react";

const PREVIEW_SAMPLE = {
  CLIENTE_NOME: "Maria Silva", REVENDEDORA_NOME: "Ana Souza", VALOR_TOTAL: "1.299,90",
  PARCELAS: "6", VALOR_PARCELA: "216,65", LINK_PROMISSORIA: "https://exemplo/eletros/assinar/ABC123",
  PARCELA_ATUAL: "2", TOTAL_PARCELAS: "6", SALDO_PRINCIPAL: "216,65", SALDO_RESTANTE: "216,65",
  VENCIMENTO: "15/07/2026", DIAS_ATRASO: "3", MULTA: "4,33", JUROS: "0,65", ENCARGOS: "4,98",
  VALOR_ATUALIZADO: "221,63", LINK_PAGAMENTO: "💳 Pague pelo link: https://mpago.la/exemplo",
  VALOR_PAGO: "216,65", DATA_PAGAMENTO: "15/07/2026", DETALHE_ENCARGOS: "",
  LINK_RECIBO: "https://exemplo/api/eletros/receipts/abc123",
};

// Preview local (NAO envia WhatsApp) da mensagem de assinatura Eletros com dados ficticios.
function EletrosPreview({ template }) {
  let text = template || "";
  for (const [k, v] of Object.entries(PREVIEW_SAMPLE)) text = text.split(`{${k}}`).join(v);
  return (
    <div className="mt-4" data-testid="eletros-preview">
      <div className="text-[10px] tracking-widest uppercase text-[#5C5A57] mb-2">Preview (dados de exemplo · nao envia)</div>
      <div className="max-w-sm rounded-2xl rounded-tl-sm bg-[#DCF8C6] p-3 text-[13px] leading-relaxed text-[#111] whitespace-pre-wrap shadow-sm" data-testid="eletros-preview-bubble">
        {text || "Digite a mensagem para ver o preview."}
      </div>
    </div>
  );
}


function Toggle({ on, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`w-11 h-6 rounded-full transition-colors relative ${on ? "bg-[#3F6B45]" : "bg-[#D6CFC7]"}`}>
      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}

const CARDS = [
  { key: "welcome_client", icon: UserCirclePlus, title: "Boas-vindas à cliente (indicação)",
    desc: "Enviada à cliente no WhatsApp quando ela se cadastra pelo link de indicação de uma revendedora.",
    placeholders: "{CLIENTE_NOME} · {REVENDEDORA_NOME} · {EMPRESA_NOME}", hasSuffix: false },
  { key: "welcome_reseller", icon: Storefront, title: "Notificação à revendedora (novo lead)",
    desc: "Avisa a revendedora que uma nova cliente se cadastrou pelo link dela.",
    placeholders: "{REVENDEDORA_NOME} · {CLIENTE_NOME} · {CLIENTE_TELEFONE} · {DATA_CADASTRO} · {EMPRESA_NOME}", hasSuffix: false },
  { key: "promissory", icon: Receipt, title: "Promissória automática (pós-venda)",
    desc: "Envia a promissória digital para a cliente automaticamente ao registrar uma venda.",
    placeholders: "{CLIENTE_NOME} · {REVENDEDORA_NOME} · {EMPRESA_NOME} · {VALOR} · {VENCIMENTO} · {LINK_PROMISSORIA} · {LINK_PAGAMENTO}", hasSuffix: true },
  { key: "eletros_promissory", icon: Lightning, title: "Assinatura da promissória — ELETROS", eletros: true,
    desc: "Operação ELETROS. Enviada à cliente ao registrar uma venda Eletros (e no reenvio de assinatura). Template separado da Cris Lingerie.",
    placeholders: "{CLIENTE_NOME} · {REVENDEDORA_NOME} · {VALOR_TOTAL} · {PARCELAS} · {VALOR_PARCELA} · {LINK_PROMISSORIA}", hasSuffix: false },
  { key: "cris_group_payment_confirmed", icon: Receipt, title: "Confirmação de PIX agrupado — CRIS LINGERIE",
    desc: "Uma única mensagem por PIX agrupado da Central de Pagamentos, enviada só após a conciliação integral de todas as parcelas.",
    placeholders: "{CLIENTE_NOME} · {VALOR_PAGO} · {QTD_PARCELAS} · {VENCIMENTO} · {EMPRESA_RESPONSAVEL}", hasSuffix: false },
];

// ELETROS - templates de confirmacao de pagamento (com recibo) + regua de cobranca (D-3 -> D+7).
const CHARGE_PLACEHOLDERS = "{CLIENTE_NOME} · {REVENDEDORA_NOME} · {PARCELA_ATUAL} · {TOTAL_PARCELAS} · {VALOR_PARCELA} · {SALDO_PRINCIPAL} · {SALDO_RESTANTE} · {VENCIMENTO} · {DIAS_ATRASO} · {MULTA} · {JUROS} · {ENCARGOS} · {VALOR_ATUALIZADO} · {LINK_PAGAMENTO}";
const ELETROS_CARDS = [
  { key: "eletros_payment_confirmed", icon: Receipt, title: "Confirmação de pagamento (integral) — ELETROS", eletros: true,
    desc: "Enviada após a baixa integral (automática ou manual). Disponibiliza o comprovante via {LINK_RECIBO}.",
    placeholders: "{CLIENTE_NOME} · {VALOR_PAGO} · {PARCELA_ATUAL} · {TOTAL_PARCELAS} · {DATA_PAGAMENTO} · {LINK_RECIBO} · {EMPRESA_RESPONSAVEL}" },
  { key: "eletros_group_payment_confirmed", icon: Receipt, title: "Confirmação de PIX agrupado — ELETROS", eletros: true,
    desc: "Número oficial compartilhado com a Cris (autorizado). Uma única mensagem por PIX agrupado, enviada só após a conciliação integral. Só dispara com a Central de Pagamentos ativa.",
    placeholders: "{CLIENTE_NOME} · {VALOR_PAGO} · {QTD_PARCELAS} · {VENCIMENTO} · {EMPRESA_RESPONSAVEL}" },
  { key: "eletros_partial_payment", icon: Receipt, title: "Confirmação de pagamento (parcial) — ELETROS", eletros: true,
    desc: "Enviada após um pagamento parcial. Mostra o saldo restante e disponibiliza o recibo via {LINK_RECIBO}.",
    placeholders: "{CLIENTE_NOME} · {VALOR_PAGO} · {SALDO_RESTANTE} · {PARCELA_ATUAL} · {TOTAL_PARCELAS} · {LINK_RECIBO} · {EMPRESA_RESPONSAVEL}" },
  { key: "eletros_charge_d_minus_3", icon: Lightning, title: "Cobrança D-3 (3 dias antes) — ELETROS", eletros: true, stage: "d_minus_3",
    desc: "Lembrete preventivo, 3 dias antes do vencimento. Sem multa/juros.", placeholders: CHARGE_PLACEHOLDERS },
  { key: "eletros_charge_d_minus_1", icon: Lightning, title: "Cobrança D-1 (véspera) — ELETROS", eletros: true, stage: "d_minus_1",
    desc: "Lembrete preventivo na véspera do vencimento. Gera o PIX. Sem encargos.", placeholders: CHARGE_PLACEHOLDERS },
  { key: "eletros_charge_due_date", icon: Lightning, title: "Cobrança D (vencimento) — ELETROS", eletros: true, stage: "due_date",
    desc: "Enviada no dia do vencimento. Ainda sem encargos indevidos.", placeholders: CHARGE_PLACEHOLDERS },
  { key: "eletros_charge_d_plus_1", icon: Lightning, title: "Cobrança D+1 (1 dia após) — ELETROS", eletros: true, stage: "d_plus_1",
    desc: "Cobrança em atraso. Usa o valor atualizado do settlement (multa + juros).", placeholders: CHARGE_PLACEHOLDERS },
  { key: "eletros_charge_d_plus_3", icon: Lightning, title: "Cobrança D+3 (3 dias após) — ELETROS", eletros: true, stage: "d_plus_3",
    desc: "Cobrança em atraso com valor atualizado do settlement.", placeholders: CHARGE_PLACEHOLDERS },
  { key: "eletros_charge_d_plus_7", icon: Lightning, title: "Cobrança D+7 (7 dias após) — ELETROS", eletros: true, stage: "d_plus_7",
    desc: "Última régua de cobrança automática. Valor atualizado do settlement.", placeholders: CHARGE_PLACEHOLDERS },
];

export default function AutomationsWhatsApp() {
  const [cfg, setCfg] = useState(null);
  const [logs, setLogs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [rem, setRem] = useState(null);
  const [remBusy, setRemBusy] = useState(false);
  const [ruler, setRuler] = useState(null);
  const [rulerBusy, setRulerBusy] = useState(false);

  const load = async () => {
    setLoadError("");
    try {
      const [{ data }, l, r, rk] = await Promise.all([
        api.get("/whatsapp/automations"),
        api.get("/whatsapp/automations/logs").catch(() => ({ data: [] })),
        api.get("/eletros/reminders/config").catch(() => ({ data: null })),
        api.get("/eletros/ruler/config").catch(() => ({ data: null })),
      ]);
      setCfg(data);
      setLogs(l.data || []);
      setRem(r.data);
      setRuler(rk.data);
    } catch (e) {
      const status = e.response?.status;
      setLoadError(
        status === 404
          ? "As rotas de automações não estão disponíveis no servidor. Atualize o backend em produção (novo deploy) e tente novamente."
          : (e.response?.data?.detail || "Não foi possível carregar as automações. Tente novamente.")
      );
    }
  };
  useEffect(() => { load(); }, []);

  const setField = (key, patch) => setCfg((c) => ({ ...c, [key]: { ...c[key], ...patch } }));

  const saveReminder = async () => {
    setRemBusy(true);
    try { const { data } = await api.put("/eletros/reminders/config", rem); setRem(data); toast.success("Lembrete Eletros salvo"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar lembrete"); }
    finally { setRemBusy(false); }
  };
  const runReminder = async () => {
    setRemBusy(true);
    try { const { data } = await api.post("/eletros/reminders/run"); toast.success(`Lembretes disparados: ${data.sent} (de ${data.candidates ?? 0} pendentes)`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao rodar lembretes"); }
    finally { setRemBusy(false); }
  };

  const saveRuler = async () => {
    setRulerBusy(true);
    try { const { data } = await api.put("/eletros/ruler/config", ruler); setRuler(data); toast.success("Régua Eletros salva"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar régua"); }
    finally { setRulerBusy(false); }
  };
  const runRuler = async () => {
    setRulerBusy(true);
    try { const { data } = await api.post("/eletros/ruler/run"); toast.success(`Régua executada: ${data.sent ?? 0} enviada(s), ${data.skipped ?? 0} ignorada(s)`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao rodar régua"); }
    finally { setRulerBusy(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      [...CARDS, ...ELETROS_CARDS].forEach((c) => { if (cfg[c.key]) payload[c.key] = cfg[c.key]; });
      await api.put("/whatsapp/automations", payload);
      toast.success("Automacoes salvas");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  if (loadError) {
    return (
      <div data-testid="automations-error" className="max-w-xl">
        <div className="border-2 border-[#B4564F] bg-[#FBEDED] p-6">
          <div className="text-[#8B5E5E] font-medium mb-2">Não foi possível carregar as automações</div>
          <p className="text-sm text-[#5C5A57]">{loadError}</p>
          <button onClick={load} className="brand mt-4" data-testid="automations-retry-btn">Tentar novamente</button>
        </div>
      </div>
    );
  }

  if (!cfg) return <div className="text-sm tracking-widest uppercase text-[#5C5A57]" data-testid="automations-loading">Carregando...</div>;

  const renderCard = (card) => {
    const c = cfg[card.key] || {};
    const Icon = card.icon;
    return (
      <section key={card.key} className={`border bg-white p-6 ${card.eletros ? "border-[#F5B400]" : "border-[#EBE5DF]"}`} data-testid={`automation-${card.key}`}>
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex items-start gap-3">
            <Icon size={22} weight="duotone" color={card.eletros ? "#C9960A" : "#3F6B45"} className="mt-0.5" />
            <div>
              <h3 className="section-title">{card.title}{card.eletros && <span className="ml-2 rounded-full px-2 py-0.5 text-[9px] font-bold align-middle" style={{ background: "#FEF3C7", color: "#B45309" }}>ELETROS</span>}</h3>
              <p className="text-xs text-[#5C5A57] mt-1 max-w-xl">{card.desc}</p>
            </div>
          </div>
          <Toggle on={c.enabled !== false} onClick={() => setField(card.key, { enabled: !(c.enabled !== false) })} testid={`toggle-${card.key}`} />
        </div>
        <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Mensagem</label>
        <textarea rows={6} value={c.template || ""} onChange={(e) => setField(card.key, { template: e.target.value })}
          className="w-full p-3 border border-[#EBE5DF] text-sm font-mono" data-testid={`template-${card.key}`} />
        {card.hasSuffix && (
          <div className="mt-3">
            <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Complemento quando houver PIX gerado</label>
            <textarea rows={2} value={c.pix_suffix || ""} onChange={(e) => setField(card.key, { pix_suffix: e.target.value })}
              className="w-full p-3 border border-[#EBE5DF] text-sm font-mono" data-testid={`suffix-${card.key}`} />
          </div>
        )}
        <div className="text-[11px] text-[#5C5A57] mt-2">Placeholders: <span className="font-mono">{card.placeholders}</span></div>
        {card.eletros && <EletrosPreview template={c.template || ""} />}
      </section>
    );
  };

  return (
    <div data-testid="automations-page">
      <div className="topbar">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">WhatsApp</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Automacoes WhatsApp</h1>
          <p className="text-sm text-[#5C5A57] mt-2 max-w-2xl">Mensagens automaticas de boas-vindas por indicacao e envio da promissoria pos-venda. Envio best-effort (nunca bloqueia cadastro ou venda).</p>
        </div>
        <button onClick={save} disabled={saving} className="brand" data-testid="automations-save-btn">{saving ? "Salvando..." : "Salvar automacoes"}</button>
      </div>

      <div className="border border-[#EBE5DF] bg-[#FBF8F5] p-4 mb-6 text-sm text-[#3D3A36]" data-testid="shared-channel-notice">
        <b>Número oficial compartilhado:</b> Cris Lingerie e Eletros usam a mesma integração de WhatsApp, com notificações configuradas de forma independente.
        Toda confirmação de pagamento leva a assinatura da empresa responsável ("Cris Lingerie – Banco das Revendedoras" / "Eletros – Banco das Revendedoras").
        Envios de outras origens (ex.: empréstimos pessoais) são bloqueados neste número.
      </div>

      <div className="space-y-6">
        {CARDS.map((card) => renderCard(card))}
      </div>

      <div className="mt-10 mb-4">
        <h2 className="font-display text-2xl font-bold tracking-tight text-[#2B1E4A]">Régua de Cobrança & Recibos — ELETROS</h2>
        <p className="text-sm text-[#5C5A57] mt-1 max-w-2xl">Mensagens de confirmação (com recibo) e a régua automática D-3 → D+7. Valores sempre vêm do settlement (fonte única). Templates independentes da Cris Lingerie.</p>
      </div>

      {ruler && (
        <section className="border border-[#F5B400] bg-white p-6 mb-6" data-testid="eletros-ruler-config">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-start gap-3">
              <Lightning size={22} weight="duotone" color="#C9960A" className="mt-0.5" />
              <div>
                <h3 className="section-title">Régua de cobrança automática
                  <span className="ml-2 rounded-full px-2 py-0.5 text-[9px] font-bold align-middle" style={{ background: "#FEF3C7", color: "#B45309" }}>ELETROS</span>
                </h3>
                <p className="text-xs text-[#5C5A57] mt-1 max-w-xl">Roda diariamente. Interrompe automaticamente ao quitar. Pagamento parcial cobra apenas o saldo restante. Só envia quando há obrigação financeira elegível.</p>
              </div>
            </div>
            <Toggle on={ruler.enabled !== false} onClick={() => setRuler((r) => ({ ...r, enabled: !(r.enabled !== false) }))} testid="toggle-eletros-ruler" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {Object.entries(ruler.stages || {}).map(([sk, sv]) => (
              <div key={sk} className="border border-[#EBE5DF] p-3 flex items-center justify-between gap-2" data-testid={`ruler-stage-${sk}`}>
                <span className="text-xs font-bold text-[#2B1E4A]">{sk.replace("d_minus_", "D-").replace("d_plus_", "D+").replace("due_date", "D")}</span>
                <Toggle on={sv.enabled !== false} onClick={() => setRuler((r) => ({ ...r, stages: { ...r.stages, [sk]: { ...sv, enabled: !(sv.enabled !== false) } } }))} testid={`toggle-ruler-${sk}`} />
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-4">
            <button onClick={saveRuler} disabled={rulerBusy} className="brand" data-testid="ruler-save-btn">{rulerBusy ? "..." : "Salvar régua"}</button>
            <button onClick={runRuler} disabled={rulerBusy} className="rounded-lg px-4 py-2 text-sm font-bold text-[#2B1E4A]" style={{ background: "#F5B400" }} data-testid="ruler-run-btn">Rodar régua agora</button>
          </div>
        </section>
      )}

      <div className="space-y-6">
        {ELETROS_CARDS.map((card) => renderCard(card))}
      </div>

      {rem && (
        <section className="border border-[#F5B400] bg-white p-6 mt-6" data-testid="eletros-reminder-config">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-start gap-3">
              <Lightning size={22} weight="duotone" color="#C9960A" className="mt-0.5" />
              <div>
                <h3 className="section-title">Lembrete automático de assinatura
                  <span className="ml-2 rounded-full px-2 py-0.5 text-[9px] font-bold align-middle" style={{ background: "#FEF3C7", color: "#B45309" }}>ELETROS</span>
                </h3>
                <p className="text-xs text-[#5C5A57] mt-1 max-w-xl">Reenvia a solicitação de assinatura para vendas Eletros que continuam pendentes. Nunca cria nova venda/promissória/parcela e para automaticamente ao assinar ou ao atingir o limite.</p>
              </div>
            </div>
            <Toggle on={rem.enabled !== false} onClick={() => setRem((r) => ({ ...r, enabled: !(r.enabled !== false) }))} testid="toggle-eletros-reminder" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[["first_reminder_days", "Dias até o 1º lembrete"], ["interval_days", "Intervalo entre lembretes (dias)"], ["max_reminders", "Limite de reenvios"]].map(([k, label]) => (
              <div key={k}>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">{label}</label>
                <input type="number" min={0} value={rem[k] ?? 0} onChange={(e) => setRem((r) => ({ ...r, [k]: Math.max(0, parseInt(e.target.value || "0", 10)) }))}
                  className="w-full p-3 border border-[#EBE5DF] text-sm" data-testid={`reminder-${k}`} />
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-4">
            <button onClick={saveReminder} disabled={remBusy} className="brand" data-testid="reminder-save-btn">{remBusy ? "..." : "Salvar lembrete"}</button>
            <button onClick={runReminder} disabled={remBusy} className="rounded-lg px-4 py-2 text-sm font-bold text-[#2B1E4A]" style={{ background: "#F5B400" }} data-testid="reminder-run-btn">Rodar agora</button>
          </div>
        </section>
      )}

      <section className="border border-[#EBE5DF] bg-white p-6 mt-6" data-testid="automation-logs">
        <h3 className="section-title mb-4 flex items-center gap-2"><ChatCircleDots size={18} weight="duotone" /> Logs de envio (ultimos 200)</h3>
        {logs.length === 0 ? (
          <div className="text-sm text-[#5C5A57]" data-testid="logs-empty">Nenhum envio registrado ainda.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] tracking-widest uppercase text-[#5C5A57] text-left border-b border-[#EBE5DF]">
                <th className="py-2 pr-4">Data</th><th className="py-2 pr-4">Tipo</th><th className="py-2 pr-4">Destinatario</th><th className="py-2 pr-4">Status</th><th className="py-2">Erro</th>
              </tr></thead>
              <tbody>
                {logs.map((g) => (
                  <tr key={g.id} className="border-b border-[#F0EBE5]" data-testid={`log-row-${g.id}`}>
                    <td className="py-2 pr-4 whitespace-nowrap text-[#5C5A57]">{(g.created_at || "").replace("T", " ").slice(0, 16)}</td>
                    <td className="py-2 pr-4">{g.type}</td>
                    <td className="py-2 pr-4">{g.client_name || g.reseller_name || g.phone}</td>
                    <td className="py-2 pr-4">
                      <span className={`badge ${g.status === "sent" ? "green" : g.status === "failed" ? "red" : ""}`}>{g.status}</span>
                    </td>
                    <td className="py-2 text-[#8B5E5E] text-xs">{g.error || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
